import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { api } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getActiveDirectoryUsers, useUsersDirectory } from '../../lib/usersDirectory';
import { ReminderFormModal, ReminderRow } from '../Reminders/ReminderFormModal';

const { FiPlus, FiBell } = FiIcons;

export default function LeadRemindersPanel({ lead, viewer }) {
  const { addToast } = useToast();
  const { users: directoryUsers } = useUsersDirectory();
  const users = useMemo(() => getActiveDirectoryUsers(directoryUsers), [directoryUsers]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!lead?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const reminders = await api.reminders.getForLead(lead.id);
      setRows(reminders || []);
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Could not load reminders', message: err.message || 'Try again.' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [lead?.id, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await api.reminders.update(editing.id, payload);
        addToast({ type: 'success', title: 'Reminder updated' });
      } else {
        await api.reminders.create({
          ...payload,
          lead_id: lead.id,
        });
        addToast({ type: 'success', title: 'Reminder created' });
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not save', message: err.message || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const complete = async (row) => {
    setBusyId(row.id);
    try {
      await api.reminders.complete(row.id);
      addToast({ type: 'success', title: 'Reminder completed' });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not complete', message: err.message || 'Try again.' });
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (row) => {
    setBusyId(row.id);
    try {
      await api.reminders.dismiss(row.id);
      addToast({ type: 'success', title: 'Reminder dismissed' });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not dismiss', message: err.message || 'Try again.' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete reminder “${row.title}”?`)) return;
    setBusyId(row.id);
    try {
      await api.reminders.delete(row.id);
      addToast({ type: 'success', title: 'Reminder deleted' });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete', message: err.message || 'Try again.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">Reminders for this lead</p>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <SafeIcon icon={FiPlus} className="h-3.5 w-3.5 mr-1" />
          Add reminder
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
          <Spinner className="text-primary" />
          Loading reminders…
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <SafeIcon icon={FiBell} className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-700">No reminders yet</p>
          <p className="text-xs text-gray-400 mt-1">Create a follow-up for yourself or a teammate.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <ReminderRow
              key={row.id}
              row={row}
              role={viewer?.role}
              viewerId={viewer?.id}
              onComplete={complete}
              onDismiss={dismiss}
              onEdit={(r) => {
                setEditing(r);
                setFormOpen(true);
              }}
              onDelete={remove}
              busyId={busyId}
              showLead={false}
            />
          ))}
        </div>
      )}

      <ReminderFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSave={save}
        saving={saving}
        users={users}
        currentUserId={viewer?.id}
        initial={editing}
      />
    </div>
  );
}
